import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, API, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LETTER } from "../constants/testIds3";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileText, DownloadSimple, FloppyDisk, Sparkle } from "@phosphor-icons/react";

async function downloadPdf(storagePath) {
  const token = getToken();
  const resp = await fetch(`${API}/files/${storagePath}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) return toast.error("Download failed");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = storagePath.split("/").pop();
  a.click();
  URL.revokeObjectURL(url);
}

export default function Letters() {
  const { user, tenantId } = useAuth();
  const isAdmin = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const [templates, setTemplates] = useState([]);
  const [letters, setLetters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ template_key: "offer_letter", employee_id: "" });
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ key: "", title: "", body: "" });

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    if (isAdmin) {
      // Merge admin-panel templates (kind-based, incl. AI-generated ones) with legacy /letter-templates (defaults)
      Promise.all([
        api.get("/letter-templates", { params }).then(({ data }) => data).catch(() => []),
        api.get("/admin/letter-templates", { params }).then(({ data }) => data).catch(() => []),
      ]).then(([legacy, admin]) => {
        const byKey = {};
        for (const t of legacy) byKey[t.key] = t;
        // Admin panel entries override — use their `kind` as key
        for (const t of admin) {
          byKey[t.kind] = { id: t.id, key: t.kind, title: t.title, body: t.body, is_default: false, source: "admin" };
        }
        setTemplates(Object.values(byKey));
      });
      api.get("/letters", { params }).then(({ data }) => setLetters(data));
      api.get("/employees", { params }).then(({ data }) => setEmployees(data));
    } else {
      api.get("/letters/mine").then(({ data }) => setLetters(data));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const generate = async () => {
    try {
      await api.post("/letters/generate", genForm);
      toast.success("Letter generated");
      setGenOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveTpl = async () => {
    try {
      const payload = { ...tplForm };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/letter-templates", payload);
      toast.success("Template saved");
      setTplOpen(false); setTplForm({ key: "", title: "", body: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editTemplate = (t) => {
    setTplForm({ key: t.key, title: t.title, body: t.body });
    setTplOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Documents</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">HR letters</h1>
          <p className="text-sm text-zinc-500 mt-2">Offer, appointment, experience, relieving and salary revision letters — one-click PDF.</p>
        </div>
        {isAdmin && (
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={LETTER.generateBtn}><Sparkle size={16} className="mr-1.5" /> Generate letter</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Generate HR letter</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Template *</Label>
                  <Select value={genForm.template_key} onValueChange={(v) => setGenForm({ ...genForm, template_key: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="letter-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                  <Select value={genForm.employee_id} onValueChange={(v) => setGenForm({ ...genForm, employee_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="letter-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGenOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={generate} className="btn-zenith rounded-sm" data-testid="letter-generate-confirm">Generate PDF</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="letters">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="letters" className="rounded-sm">Issued letters</TabsTrigger>
          {isAdmin && <TabsTrigger value="templates" className="rounded-sm">Templates</TabsTrigger>}
        </TabsList>

        <TabsContent value="letters" className="mt-4">
          <div className="overflow-x-auto border border-zinc-200 rounded-sm" data-testid={LETTER.list}>
            <table className="hr-table">
              <thead><tr>{isAdmin && <th>Employee</th>}<th>Template</th><th>Title</th><th>Issued</th><th></th></tr></thead>
              <tbody>
                {letters.length === 0 && <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-zinc-500 text-sm">No letters generated yet.</td></tr>}
                {letters.map((l) => (
                  <tr key={l.id}>
                    {isAdmin && <td><div className="text-sm">{l.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{l.employee_code}</div></td>}
                    <td><Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300 font-mono-plex text-xs">{l.template_key}</Badge></td>
                    <td>{l.title}</td>
                    <td className="font-mono-plex text-xs">{new Date(l.generated_at).toLocaleString()}</td>
                    <td>
                      <button onClick={() => downloadPdf(l.storage_path)} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`letter-download-${l.id}`}>
                        <DownloadSimple size={12} className="inline mr-1" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="templates" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={LETTER.templates}>
              {templates.map((t) => (
                <div key={t.key} className="border border-zinc-200 rounded-sm p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-sm bg-emerald-50 grid place-items-center text-emerald-800"><FileText size={20} weight="duotone" /></div>
                      <div>
                        <div className="text-xs font-mono-plex text-zinc-500">{t.key}</div>
                        <div className="font-display font-bold">{t.title}</div>
                      </div>
                    </div>
                    {t.is_default && <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300">default</Badge>}
                    {t.source === "admin" && <Badge variant="outline" className="rounded-sm bg-adb-green/10 border-adb-green/40 text-adb-navy">customized</Badge>}
                  </div>
                  <div className="mt-3 text-xs text-zinc-600 max-h-24 overflow-hidden line-clamp-4" dangerouslySetInnerHTML={{ __html: t.body }} />
                  <div className="mt-3">
                    <button onClick={() => editTemplate(t)} className="text-xs text-emerald-700 hover:underline" data-testid={`template-edit-${t.key}`}>Customize →</button>
                  </div>
                </div>
              ))}
            </div>

            <Dialog open={tplOpen} onOpenChange={setTplOpen}>
              <DialogContent className="max-w-3xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">Customize template · <span className="font-mono-plex text-sm">{tplForm.key}</span></DialogTitle></DialogHeader>
                <div className="grid grid-cols-1 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} className="rounded-sm" /></div>
                  <div className="space-y-1">
                    <Label className="text-xs">Body (HTML with placeholders like {"{{ employee_name }}"} , {"{{ designation }}"}, {"{{ joining_date }}"}, {"{{ ctc_annual }}"}, {"{{ tenant_name }}"}, {"{{ today }}"})</Label>
                    <Textarea rows={16} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} className="rounded-sm font-mono-plex text-xs" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTplOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={saveTpl} className="btn-zenith rounded-sm" data-testid="template-save-btn"><FloppyDisk size={14} className="mr-1" /> Save template</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
